import type { CatalogItemInstanceMember } from "../../../../cbi-generator/generated/dto/CatalogItemInstanceMember";
import type { CatalogItemInstanceStaff } from "../../../../cbi-generator/generated/dto/CatalogItemInstanceStaff";
import type { CatalogItemMember } from "../../../../cbi-generator/generated/dto/CatalogItemMember";
import type { CatalogItemStaff } from "../../../../cbi-generator/generated/dto/CatalogItemStaff";
import { ItemType } from "../../../../cbi-generator/generated/dto/ItemType";

export type OnSaleItemInstance = CatalogItemInstanceMember | CatalogItemInstanceStaff;

/** An on-sale catalog item instance (from `onSaleItemInstances`) with its loaded item. */
export class ProductListing {
  readonly catalogItem: CatalogItemMember | CatalogItemStaff;
  readonly instanceId: number;
  readonly assignId: number;
  readonly itemType: ItemType | null;
  readonly itemName: string | null;
  readonly title: string | null;
  readonly description: string | null;
  readonly imageURL: string | null;
  readonly membershipTypeId: number | null;
  readonly damageWaiverTypeId: number | null;
  readonly priceCents: number | null;
  readonly rawPrice: number;
  readonly allowPriceOverride: boolean;
  readonly priceOverrideMin: number | null;
  readonly priceOverrideMax: number | null;
  readonly isSingular: boolean;

  constructor(readonly catalogItemInstance: OnSaleItemInstance) {
    const item = catalogItemInstance.itemId;
    if (!item)
      throw new Error(
        `Catalog item instance ${catalogItemInstance.assignId} is missing its item.`,
      );
    this.catalogItem = item;
    this.instanceId = catalogItemInstance.assignId;
    this.assignId = item.assignId;
    this.itemType = item.itemType;
    this.itemName = item.itemName;
    this.title = item.itemName;
    this.description = item.itemDescription;
    this.imageURL = item.imageUrl;
    this.membershipTypeId = item.membershipTypeId;
    this.damageWaiverTypeId = item.damageWaiverTypeId;
    this.priceCents = catalogItemInstance.priceCents;
    this.rawPrice = catalogItemInstance.priceCents ?? 0;
    this.allowPriceOverride =
      (catalogItemInstance.allowPriceOverride ?? item.allowPriceOverride) ===
      true;
    this.priceOverrideMin =
      catalogItemInstance.priceOverrideMin ?? item.priceOverrideMin;
    this.priceOverrideMax =
      catalogItemInstance.priceOverrideMax ?? item.priceOverrideMax;
    this.isSingular =
      item.itemType !== ItemType.PRODUCT ||
      catalogItemInstance.maxQuantity === 1;
  }

  get price() {
    return this.rawPrice / 100;
  }
  hasPerson() {
    return (
      this.isMembershipProduct() ||
      this.isDamageWaiverProduct() ||
      this.isCardProduct() ||
      this.isAPClassSignupProduct() ||
      this.isGiftCardProduct() ||
      this.isDonationProduct()
    );
  }
  getIsStockedItem() {
    return this.itemType === ItemType.PRODUCT;
  }
  isCardProduct() {
    return this.itemType === ItemType.CARD;
  }
  isDonationProduct() {
    return this.itemType === ItemType.DONATION;
  }
  isMembershipProduct() {
    return this.itemType === ItemType.MEMBERSHIP;
  }
  isDamageWaiverProduct() {
    return this.itemType === ItemType.DAMAGE_WAIVER;
  }
  isGuestPrivsProduct() {
    return this.itemType === ItemType.GUEST_PRIVS;
  }
  isGiftCardProduct() {
    return this.itemType === ItemType.GIFT_CARD;
  }
  isAPClassSignupProduct() {
    return this.itemType === ItemType.PAID_CLASS;
  }
}